import React from 'react';
import { translate } from 'react-i18next';

import SidebarNoData from 'components/ScheduleSidebar/SidebarNoData.js';
import ShiftDetailBlock from 'components/ScheduleSidebar/ShiftDetailBlock.js';
import UnavailableSidebarItem from 'components/UnavailableSidebarItem.js';
import { describeUnavailable } from 'components/ScheduleSidebar/unavailableRows.js';

// Copy for a submission a manager has decided on. `draft` and `submitted` are handled before this
// is consulted.
const DECIDED_COPY = {
  approved: 'SelfScheduleSidebar.approved_status',
  deferred: 'SelfScheduleSidebar.deferred_status',
  rejected: 'SelfScheduleSidebar.rejected_status',
};

// Why picking is shut, if it is. `upcoming` says when, if the API knows.
const WINDOW_COPY = {
  upcoming: 'SelfScheduleSidebar.window_not_open',
  not_assigned: 'SelfScheduleSidebar.no_pick_group',
  ended: 'SelfScheduleSidebar.window_closed',
};

// One tinted box per sentence.
function renderAlerts(messages, status) {
  if (messages.length === 0) {
    return false;
  }

  return (
    <div className="self-schedule__alerts">
      {messages.map(message => <p key={message} className={`is-${status}`}>{message}</p>)}
    </div>
  );
}

// A refused write's own sentences, which block by definition. A submit the check blocked carries
// its check instead, and a refusal that names nothing still gets a line.
function renderRefusal(t, error) {
  if (!error || error.check) {
    return false;
  }

  return renderAlerts(
    error.messages.length > 0 ? error.messages : [t('SelfScheduleSidebar.write_failed')],
    'blocking',
  );
}

// The submit check, laid out like the iOS app's review screen: a row per obligation with the icon
// for the status the API resolved, then each finding no row says, red where it stopped the submit
// and amber where it did not. A check with nothing to report still says so.
function renderCheck(t, check) {
  const rows = check.obligations.concat(check.errors, check.warnings).length > 0
    ? check.obligations
    : [{ key: 'none', status: 'met', message: t('SelfScheduleSidebar.no_issues_found') }];

  return (
    <div className="self-schedule__check">
      <span className="calendar-input-bullet small">
        {t('SelfScheduleSidebar.submission_check')}
      </span>
      {rows.length > 0 && (
        <ul className="self-schedule__checklist">
          {rows.map(row => (
            <li key={row.key} className={`is-${row.status}`}>{row.message || row.name}</li>
          ))}
        </ul>
      )}
      {renderAlerts(check.errors, 'blocking')}
      {renderAlerts(check.warnings, 'warning')}
    </div>
  );
}

// One row per condition on a shift, coloured by the resolved enforcement.
function renderWarnings(warnings) {
  if (warnings.length === 0) {
    return false;
  }

  return (
    <ul className="self-schedule__warnings">
      {warnings.map(warning => (
        <li key={`${warning.code}-${warning.message}`} className={`is-${warning.enforcement}`}>
          {warning.message}
        </li>
      ))}
    </ul>
  );
}

// Laid out like the schedule page's shift details panel: title, breaks, then the detail lines.
// The row carries the panel's content class so the panel's own paragraph rules style them.
function renderShift(props, shift) {
  const { blockData, busy, t } = props;
  const skills = (shift.skills || []).map(skill => skill.name).join(', ');
  const details = [
    shift.job_site && shift.job_site.name,
    shift.position && shift.position.name,
    skills && t('SelfScheduleSidebar.skills', { skills }),
  ].filter(Boolean);

  return (
    <li key={shift.id} className="shift-detail-content">
      <p className="shift-detail-title">
        {shift.shift_type_name}
        {shift.shift_type_name && <br />}
        {shift.time}
      </p>
      {shift.total_breaks > 0 && (
        <p className="shift-detail-breaks">
          {t('SelfScheduleSidebar.breaks', { minutes: shift.total_breaks })}
        </p>
      )}
      {details.length > 0 && (
        <p className="shift-details-meta">
          {details.map(detail => <span key={detail}>{detail}</span>)}
        </p>
      )}
      {renderWarnings(shift.warnings || [])}
      {shift.held && blockData.canPick && (
        <button
          type="button"
          className="calendar-input-button"
          disabled={busy}
          onClick={() => props.onCancelRequest(shift)}
        >
          {t('SelfScheduleSidebar.cancel_request')}
        </button>
      )}
      {!shift.held && blockData.canPick && (
        <button
          type="button"
          className="calendar-input-button action-button"
          disabled={busy || shift.blocked}
          onClick={() => props.onRequestShift(shift)}
        >
          {t(shift.blocked ? 'SelfScheduleSidebar.not_selectable' : 'SelfScheduleSidebar.request_shift')}
        </button>
      )}
    </li>
  );
}

// One panel per state with the shifts as rows, the way the schedule page lists a day's time off.
function renderShifts(props, shifts, held) {
  if (shifts.length === 0) {
    return false;
  }

  return (
    <div className="calendar-input shift-details">
      {/* Red while only eligible, matching the calendar chips; blue once selected. */}
      <span className={`calendar-input-bullet ${held ? 'shift-bullet' : 'available-bullet'}`}>
        {props.t(held ? 'SelfScheduleSidebar.selected_shifts' : 'SelfScheduleSidebar.eligible_shifts')}
      </span>
      <div className="data-list">
        <ul>{shifts.map(shift => renderShift(props, shift))}</ul>
      </div>
    </div>
  );
}

// A roster shift, laid out like the schedule page's shift details panel, down to its detail block,
// which resolves the department and location names itself.
function renderRosterShift(props, shift) {
  const { t } = props;

  return (
    <li key={`shift-${shift.id}`} className="shift-detail-content">
      <p className="shift-detail-title">
        {shift.name}
        {shift.name && <br />}
        {shift.time}
      </p>
      {shift.breaks && shift.breaks.length > 0 && (
        <p className="shift-detail-breaks">
          {`${t('SidebarShiftDetails.breaks')}: ${Utils.formatUnformattedBreaks(shift.breaks)}`}
        </p>
      )}
      <ShiftDetailBlock
        departmentID={shift.department_id}
        locationID={shift.location_id}
        position={shift.position}
        job_site_name={shift.job_site_name}
        job_site_address={shift.job_site_address}
      />
      {/* The list's own notes style, from its time off rows, not the panel's ruled block. */}
      {shift.shift_notes && (
        <span className="time-off-notes">
          <strong>{t('UnavailableSidebarItem.notes')}</strong>
          {shift.shift_notes}
        </span>
      )}
    </li>
  );
}

// A time off entry, as the schedule page's time off panel lists it: the same row, resolved the
// same way. The overlay carries approved entries only, so the state label never reads pending.
// N/A days are shown here, never changed: with no handler the row offers no Remove N/A.
function renderTimeOff(props, entry) {
  return <UnavailableSidebarItem key={`off-${entry.id}`} {...describeUnavailable(entry, props.t)} />;
}

// What the employee already has on this day from outside the block: their roster, approved time
// off, and any N/A day they marked. This is why a day can show no pool at all.
function renderOwnDay(props) {
  const { day, t } = props;
  if (day.roster.length === 0 && day.timeOff.length === 0 && !day.naEntry) {
    return false;
  }

  return (
    <div className="calendar-input shift-details">
      <span className="calendar-input-bullet scheduled">{t('SelfScheduleSidebar.your_schedule')}</span>
      <div className="data-list timeoff-requests">
        <ul>
          {day.roster.map(shift => renderRosterShift(props, shift))}
          {day.timeOff.map(entry => renderTimeOff(props, entry))}
          {day.naEntry && renderTimeOff(props, day.naEntry)}
        </ul>
      </div>
    </div>
  );
}

function renderDay(props) {
  const {
    blockData, date, day, dayError, t,
  } = props;
  const { block } = blockData;
  const dayMoment = moment(date);
  const inBlock = dayMoment.isBetween(block.block_start_date, block.block_end_date, 'day', '[]');

  return (
    <div>
      <div className="self-schedule__day-header">
        <h2>{Utils.formatLocaleShortDate(dayMoment)}</h2>
        <p>{dayMoment.format('dddd')}</p>
      </div>
      {renderRefusal(t, dayError)}
      {renderOwnDay(props)}
      {renderShifts(props, day.shifts.filter(shift => shift.held), true)}
      {renderShifts(props, day.shifts.filter(shift => !shift.held), false)}
      {day.shifts.length === 0 && inBlock && (
        <p className="self-schedule__empty-day">{t('SelfScheduleSidebar.no_shifts_this_day')}</p>
      )}
    </div>
  );
}

function renderPicks(props) {
  const { blockData, t } = props;
  const { picks } = blockData;

  return (
    <div className="calendar-input">
      <span className="calendar-input-bullet shift-bullet">{t('SelfScheduleSidebar.your_selections')}</span>
      {picks.length === 0 && (
        <p className="self-schedule__note">{t('SelfScheduleSidebar.no_selections')}</p>
      )}
      {picks.length > 0 && (
        <div className="data-list shifts">
          <ul>
            {picks.map(pick => (
              <li key={pick.id}>
                <button
                  type="button"
                  className="self-schedule__link"
                  onClick={() => props.onSelectDate(pick.date)}
                >
                  {Utils.formatLocaleDate(moment(pick.date))}
                </button>
                <span>{pick.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function submissionStatus(t, submission, isDraft) {
  if (isDraft) {
    return t('SelfScheduleSidebar.not_submitted');
  }
  // `submitted` is the one status awaiting review; the API sends no separate flag for it.
  if (submission.status === 'submitted') {
    return t('SelfScheduleSidebar.submitted_on', {
      date: Utils.formatLocaleDate(moment(submission.submitted_at)),
    });
  }
  return t(DECIDED_COPY[submission.status]);
}

function windowNote(t, blockData) {
  const { windowState, windowOpensAt } = blockData;
  if (windowState === 'open') {
    return false;
  }

  const message = windowState === 'upcoming' && windowOpensAt
    ? t('SelfScheduleSidebar.window_opens_at', {
      date: Utils.formatLocaleDate(windowOpensAt),
      time: windowOpensAt.format('h:mm a'),
    })
    : t(WINDOW_COPY[windowState]);

  return <p className="self-schedule__note">{message}</p>;
}

// No desktop design exists for submitting, so it follows the iOS app's review screen. A refused
// submit is the pre-submit check the spec asks for — the API runs it in full without handing
// anything in — so the same checklist renders from the refusal and, after an accepted submit,
// from the stored validation_summary. A refused reopen has only the API's own sentence: all three
// causes share one code and the deciding department flag is not employee-readable.
function renderSubmission(props) {
  const {
    blockData, submissionError, busy, t,
  } = props;
  const { submission, canPick } = blockData;
  const isDraft = !submission || submission.status === 'draft';
  // A draft holds a stored summary only after a refused submit, and the next change leaves its
  // figures stale, so only a handed-in submission's summary is shown.
  // ponytail: a handed-in summary goes stale the same way, since a submitted package stays
  // editable while the window is open and the API re-checks only on submit. Comparing
  // tentative_block with the live picks would catch it.
  const check = (submissionError && submissionError.check)
    || (!isDraft && SelfScheduleStore.describeCheck(submission.validation_summary));

  return (
    <div className="calendar-input">
      <span className="calendar-input-bullet shift-bullet">{t('SelfScheduleSidebar.submission')}</span>
      <p className="self-schedule__status">{submissionStatus(t, submission, isDraft)}</p>
      {windowNote(t, blockData)}
      {submission && submission.resolution_note && (
        <p className="self-schedule__note">{submission.resolution_note}</p>
      )}
      {renderRefusal(t, submissionError)}
      {check && renderCheck(t, check)}
      {canPick && isDraft && (
        <button
          type="button"
          className="calendar-input-button action-button"
          disabled={busy}
          onClick={props.onSubmit}
        >
          {t('SelfScheduleSidebar.submit')}
        </button>
      )}
      {canPick && !isDraft && !submission.change_limit_reached && (
        <button
          type="button"
          className="calendar-input-button"
          disabled={busy}
          onClick={props.onReopen}
        >
          {t('SelfScheduleSidebar.reopen')}
        </button>
      )}
    </div>
  );
}

// Props: blockData (as SelfScheduleStore resolved it), date and day (the selected day's bucket),
// dayError and submissionError ({messages, check} or null), busy while a write is in flight,
// and the on* handlers.
function SelfScheduleSidebar(props) {
  const { blockData, date } = props;

  return (
    <aside>
      <div>
        <div className="sidebarContainer">
          {date ? renderDay(props) : <SidebarNoData active />}
          {/* Once published the selections are real shifts on the roster overlay, so a picks list
              here would read as empty for no reason. */}
          {!blockData.published && renderPicks(props)}
          {renderSubmission(props)}
        </div>
      </div>
    </aside>
  );
}

export default translate()(SelfScheduleSidebar);
